import React, { useMemo } from 'react';
import { Box, Card, Table, TableHead, TableRow, TableCell, TableBody, Typography, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ReferenceLine,
  LabelList,
} from 'recharts';

import {
  RADIUS,
  TONE,
  makeFormatters,
  fmtPct,
  fmtX,
  deltaProps,
  useSeries,
  Stat,
  Section,
  ChartCard,
  Callout,
  useAxis,
  ChartTip,
  legendStyle,
  BAR_END,
  StatementTable,
  DashboardShell,
} from './financeDashboardKit';

import {
  COMPANY,
  YEARS,
  YEAR_META,
  GROUP,
  GROUP_HALF,
  H1_26_POSITION,
  MARKET,
  PEER_PAT_MARGIN,
  GOVT_CONTRIBUTIONS_FY25,
  BS_PERIODS,
  BALANCE_SHEET,
  PL_PERIODS,
  PROFIT_AND_LOSS,
  CASH_FLOW,
  SEGMENT_YEARS,
  SEGMENT_NAMES,
  SEGMENTS,
  OPS_PERIODS,
  OPERATIONS,
  H1_26_ONE_OFFS,
} from '../constants/petronasFinancials';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'segments', label: 'Segments' },
  { key: 'cash', label: 'Cash & Funding' },
  { key: 'operations', label: 'Operations' },
  { key: 'statements', label: 'Statements' },
];

// Source figures are MYR (ringgit) million; the unit toggle converts to MYR billion.
const UNITS = {
  bn: { label: 'MYR Bn', factor: 0.001, digits: 1, suffix: 'Bn' },
  mn: { label: 'MYR Mn', factor: 1, digits: 0, suffix: 'Mn' },
};

const { fmtAmt, fmtAxis, fmtCell } = makeFormatters(UNITS, 'MYR');

// ---------- derivations ----------

function buildAnalytics() {
  const g = GROUP;
  const n = YEARS.length;
  const map = (fn) => YEARS.map((_, i) => fn(i));
  const bs = BALANCE_SHEET;
  // BS columns are [FY22..FY25, 1H26]; the first four line up with YEARS.
  const borrowings = bs.ncBorrowings.map((v, i) => v + bs.cBorrowings[i]);
  const netCash = bs.cash.map((v, i) => v - borrowings[i]);

  const fcf = map((i) => g.cffo[i] - g.ppeCapex[i]);
  const fcfHalf = GROUP_HALF.cffo.map((v, i) => v - GROUP_HALF.ppeCapex[i]);

  return {
    latest: n - 1,
    prior: n - 2,
    borrowings,
    netCash,
    ebitdaMargin: map((i) => (g.ebitda[i] / g.totalRevenue[i]) * 100),
    patMargin: map((i) => (g.totalPat[i] / g.totalRevenue[i]) * 100),
    cashConversion: map((i) => (g.cffo[i] / g.ebitda[i]) * 100),
    fcf,
    fcfHalf,
    dividendCover: map((i) => fcf[i] / g.dividendsToShareholders[i]),
    revenueCagr: (Math.pow(g.totalRevenue[n - 1] / g.totalRevenue[0], 1 / (n - 1)) - 1) * 100,
    patCagr: (Math.pow(g.totalPat[n - 1] / g.totalPat[0], 1 / (n - 1)) - 1) * 100,
    performance: map((i) => ({
      period: YEARS[i],
      revenue: g.totalRevenue[i],
      ebitda: g.ebitda[i],
      pat: g.totalPat[i],
    })),
    margins: map((i) => ({
      period: YEARS[i],
      ebitdaMargin: (g.ebitda[i] / g.totalRevenue[i]) * 100,
      patMargin: (g.totalPat[i] / g.totalRevenue[i]) * 100,
    })),
    returns: map((i) => ({ period: YEARS[i], roace: g.roacePct[i], gearing: g.gearingPct[i] })).concat([
      { period: '1H26', roace: H1_26_POSITION.roacePct, gearing: H1_26_POSITION.gearingPct },
    ]),
    cashUses: map((i) => ({
      period: YEARS[i],
      cffo: g.cffo[i],
      capex: g.capitalInvestments[i],
      dividends: g.dividendsToShareholders[i],
    })),
    liquidity: BS_PERIODS.map((p, i) => ({
      period: p,
      cash: bs.cash[i],
      borrowings: borrowings[i],
      netCash: netCash[i],
    })),
    halfCompare: [
      { metric: 'Revenue', '1H25': GROUP_HALF.totalRevenue[0], '1H26': GROUP_HALF.totalRevenue[1] },
      { metric: 'EBITDA', '1H25': GROUP_HALF.ebitda[0], '1H26': GROUP_HALF.ebitda[1] },
      { metric: 'PAT', '1H25': GROUP_HALF.totalPat[0], '1H26': GROUP_HALF.totalPat[1] },
      { metric: 'CFFO', '1H25': GROUP_HALF.cffo[0], '1H26': GROUP_HALF.cffo[1] },
      { metric: 'Capital inv.', '1H25': GROUP_HALF.capitalInvestments[0], '1H26': GROUP_HALF.capitalInvestments[1] },
    ],
  };
}

// Segment rows for a chart: one row per period, one key per segment.
const segmentRows = (field) => [
  ...SEGMENT_YEARS.map((p, i) => {
    const row = { period: p };
    SEGMENT_NAMES.forEach((s) => (row[s] = SEGMENTS[s][field][i]));
    return row;
  }),
  ...['1H25', '1H26'].map((p, i) => {
    const row = { period: p };
    SEGMENT_NAMES.forEach((s) => (row[s] = SEGMENTS[s].half[field][i]));
    return row;
  }),
];

// ---------- tab views ----------

const OverviewTab = ({ a, unit }) => {
  const axis = useAxis();
  const c = useSeries();
  const theme = useTheme();
  const g = GROUP;
  const h = GROUP_HALF;
  const { latest: L, prior: P } = a;
  const money = (v) => fmtAmt(v, unit);
  const tipMoney = (v, name) => [fmtAmt(v, unit), name];

  return (
    <>
      <Section title="FY2025 at a glance" caption="Year ended 31 December 2025 against FY2024. Revenue and PAT include discontinued operations in FY2024.">
        <Stat hero label="Revenue" value={money(g.totalRevenue[L])} {...deltaProps(g.totalRevenue[L], g.totalRevenue[P])} sub="YoY" title="Continuing-operations decline is 12.8% (305,131 → 266,135)" />
        <Stat hero label="EBITDA" value={money(g.ebitda[L])} {...deltaProps(g.ebitda[L], g.ebitda[P])} sub={`${fmtPct(a.ebitdaMargin[L])} margin`} tone="positive" />
        <Stat hero label="Profit after tax" value={money(g.totalPat[L])} {...deltaProps(g.totalPat[L], g.totalPat[P])} sub={`${fmtPct(a.patMargin[L])} margin`} tone="positive" />
        <Stat hero label="Cash from operations" value={money(g.cffo[L])} {...deltaProps(g.cffo[L], g.cffo[P])} sub="YoY" tone="positive" />
        <Stat hero label="Total assets" value={money(BALANCE_SHEET.totalAssets[3])} {...deltaProps(BALANCE_SHEET.totalAssets[3], BALANCE_SHEET.totalAssets[2])} sub="YoY" />
        <Stat hero label="Shareholders' equity" value={money(BALANCE_SHEET.shareholdersEquity[3])} {...deltaProps(BALANCE_SHEET.shareholdersEquity[3], BALANCE_SHEET.shareholdersEquity[2])} sub="YoY" tone="positive" />
      </Section>

      <Section title="First half 2026" caption="Six months to 30 June 2026 against 1H 2025.">
        <Stat label="Revenue" value={money(h.totalRevenue[1])} {...deltaProps(h.totalRevenue[1], h.totalRevenue[0])} />
        <Stat label="EBITDA" value={money(h.ebitda[1])} {...deltaProps(h.ebitda[1], h.ebitda[0])} tone="positive" />
        <Stat label="Profit after tax" value={money(h.totalPat[1])} {...deltaProps(h.totalPat[1], h.totalPat[0])} tone="warning" title="Contains a ~MYR 14.8 Bn non-cash JV loss recognition and upstream divestment gains" />
        <Stat label="Cash from operations" value={money(h.cffo[1])} {...deltaProps(h.cffo[1], h.cffo[0])} tone="positive" />
        <Stat label="Capital investments" value={money(h.capitalInvestments[1])} {...deltaProps(h.capitalInvestments[1], h.capitalInvestments[0], false)} tone="warning" title="Mainly Downstream's additional investment in a joint venture (PRefChem)" />
        <Stat label="Average Brent" value={`US$${MARKET['1H26'].brent}`} {...deltaProps(MARKET['1H26'].brent, MARKET['1H25'].brent)} sub="/bbl" />
        <Stat label="Gearing" value={fmtPct(H1_26_POSITION.gearingPct)} sub="as at 30 Jun 2026" />
        <Stat label="ROACE" value={fmtPct(H1_26_POSITION.roacePct)} sub="12-month basis" />
      </Section>

      <Callout tone="warning">
        <b>Read 1H 2026 profit with care.</b> PAT rose only MYR 1.0 Bn, but that net figure hides two large one-offs.
        A non-cash charge of about {fmtAmt(H1_26_ONE_OFFS.prefchemLossRecognised, 'bn')} recognised previously
        unrecognised losses of the PRefChem joint venture after an equity injection. That pushed Downstream to a
        loss of {fmtAmt(-SEGMENTS.Downstream.half.pat[1], 'bn')} (about {fmtAmt(H1_26_ONE_OFFS.downstreamPatExOneOff, 'bn')} profit
        without it). Upstream divestment and dilution gains pulled the other way.
      </Callout>

      <Section title="Four-year trend" caption={`Every headline line fell each year from FY22 to FY25: revenue CAGR ${fmtPct(a.revenueCagr)}, PAT CAGR ${fmtPct(a.patCagr)}. Prices fell, and the ringgit strengthened.`}>
        <ChartCard title="Revenue, EBITDA and PAT" caption={`${UNITS[unit].label}, financial years ended 31 December.`}>
          <ResponsiveContainer>
            <BarChart data={a.performance} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="revenue" name="Revenue" fill={c[0]} radius={BAR_END} />
              <Bar dataKey="ebitda" name="EBITDA" fill={c[1]} radius={BAR_END} />
              <Bar dataKey="pat" name="PAT" fill={c[2]} radius={BAR_END} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Margins" caption="EBITDA and PAT as a share of total revenue.">
          <ResponsiveContainer>
            <LineChart data={a.margins} margin={{ top: 16, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => `${Math.round(v)}%`} domain={[0, 'auto']} />
              <ChartTip formatter={(v, n) => [fmtPct(v), n]} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="ebitdaMargin" name="EBITDA margin" stroke={c[1]} strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="patMargin" name="PAT margin" stroke={c[2]} strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="1H 2026 against 1H 2025" caption={UNITS[unit].label}>
          <ResponsiveContainer>
            <BarChart data={a.halfCompare} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="metric" {...axis} interval={0} />
              <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="1H25" name="1H 2025" fill={alpha(c[0], 0.45)} radius={BAR_END} />
              <Bar dataKey="1H26" name="1H 2026" fill={c[0]} radius={BAR_END} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Returns and gearing" caption="ROACE and gearing ratio, as reported by PETRONAS.">
          <ResponsiveContainer>
            <LineChart data={a.returns} margin={{ top: 16, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => `${Math.round(v)}%`} domain={[0, 'auto']} />
              <ChartTip formatter={(v, n) => [fmtPct(v), n]} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="roace" name="ROACE" stroke={c[0]} strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="gearing" name="Gearing" stroke={c[3]} strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>

      <Section title="Benchmark" caption="PAT margin as published in the FY2025 results presentation. Peers are BP, Chevron, ConocoPhillips, Eni, Equinor, ExxonMobil, PTT, Saudi Aramco, Shell and TotalEnergies.">
        {PEER_PAT_MARGIN.years.map((y, i) => (
          <Stat key={y} label={`PAT margin ${y}`} value={`${PEER_PAT_MARGIN.petronas[i]}%`} sub="PETRONAS, in line with peer group" tone="positive" />
        ))}
        <Box sx={{ flex: '1 1 100%', fontSize: 11.5, color: theme.palette.text.secondary }}>
          The presentation shows PETRONAS within the peer range in all three years. The peer averages are drawn
          as a chart only, so no peer figures are quoted here.
        </Box>
      </Section>
    </>
  );
};

const SegmentsTab = ({ unit }) => {
  const axis = useAxis();
  const c = useSeries();
  const theme = useTheme();
  const tipMoney = (v, name) => [fmtAmt(v, unit), name];
  const patRows = segmentRows('pat');
  const revRows = segmentRows('revenue');
  const capexRows = segmentRows('capex');

  const segmentChart = (rows, stacked) => (
    <ResponsiveContainer>
      <BarChart data={rows} barGap={2} stackOffset={stacked ? 'sign' : undefined}>
        <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
        <XAxis dataKey="period" {...axis} />
        <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
        <ReferenceLine y={0} stroke={alpha(theme.palette.text.secondary, 0.6)} />
        <ChartTip formatter={tipMoney} />
        <Legend wrapperStyle={legendStyle} />
        {SEGMENT_NAMES.map((s, i) => (
          <Bar
            key={s}
            dataKey={s}
            fill={c[i]}
            stackId={stacked ? 'seg' : undefined}
            stroke={stacked ? theme.palette.background.paper : undefined}
            strokeWidth={stacked ? 2 : 0}
            radius={stacked ? 0 : BAR_END}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <>
      <Callout tone="neutral">
        Segments are shown on the current four-segment basis for FY23–FY25 (FY23 restated) and both halves.
        FY22 used an older &ldquo;Gas&rdquo; segment and is not comparable, so it is left out. Segment revenue
        includes inter-segment sales.
      </Callout>

      <Section title="Where the profit comes from">
        <ChartCard title="Segment PAT" caption={`${UNITS[unit].label}. Upstream and Gas & Maritime carry the Group; Downstream has been loss-making since FY24.`} full height={320}>
          {segmentChart(patRows, false)}
        </ChartCard>
        <ChartCard title="Segment revenue" caption={`${UNITS[unit].label}, gross of inter-segment sales.`}>
          {segmentChart(revRows, true)}
        </ChartCard>
        <ChartCard title="Capital investments by segment" caption={`${UNITS[unit].label}. 1H26 Downstream is mainly the PRefChem equity injection.`}>
          {segmentChart(capexRows, true)}
        </ChartCard>
      </Section>

      <Section title="Segment table">
        <Card
          elevation={0}
          sx={{ flex: '1 1 100%', p: 2, borderRadius: RADIUS.panel, border: `1px solid ${theme.palette.divider}`, overflowX: 'auto' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Segment · {UNITS[unit].label}</TableCell>
                {[...SEGMENT_YEARS, '1H25', '1H26'].map((p) => (
                  <TableCell key={p} align="right" sx={{ fontWeight: 700, fontSize: 12 }}>
                    {p}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {SEGMENT_NAMES.map((s, si) =>
                ['revenue', 'pat', 'capex'].map((f) => {
                  const vals = [...SEGMENTS[s][f], ...SEGMENTS[s].half[f]];
                  return (
                    <TableRow key={s + f}>
                      <TableCell sx={{ fontSize: 12.5 }}>
                        {f === 'revenue' && (
                          <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '2px', bgcolor: c[si], mr: 1 }} />
                        )}
                        {f === 'revenue' ? <b>{s}</b> : <Box component="span" sx={{ pl: 2.25 }}>{f === 'pat' ? 'PAT / (LAT)' : 'Capital investments'}</Box>}
                        {f === 'revenue' && ' — revenue'}
                      </TableCell>
                      {vals.map((v, i) => (
                        <TableCell key={i} align="right" sx={{ fontSize: 12.5, color: v < 0 ? TONE.negative : undefined, fontVariantNumeric: 'tabular-nums' }}>
                          {fmtCell(v, unit)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      </Section>
    </>
  );
};

const CashTab = ({ a, unit }) => {
  const axis = useAxis();
  const c = useSeries();
  const theme = useTheme();
  const g = GROUP;
  const L = a.latest;
  const money = (v) => fmtAmt(v, unit);
  const tipMoney = (v, name) => [fmtAmt(v, unit), name];

  return (
    <>
      <Section title="Cash generation and uses" caption="Free cash flow here is cash from operations less purchases of property, plant, equipment and intangibles.">
        <Stat hero label="Free cash flow FY25" value={money(a.fcf[L])} {...deltaProps(a.fcf[L], a.fcf[L - 1])} sub="YoY" tone="positive" />
        <Stat hero label="Dividend cover FY25" value={fmtX(a.dividendCover[L])} sub={`FCF / ${fmtAmt(g.dividendsToShareholders[L], 'bn')} dividend paid`} tone="warning" />
        <Stat hero label="Free cash flow 1H26" value={money(a.fcfHalf[1])} {...deltaProps(a.fcfHalf[1], a.fcfHalf[0])} sub="vs 1H25" tone="positive" />
        <Stat hero label="Net cash 1H26" value={money(a.netCash[4])} sub={`Cash ${fmtAmt(BALANCE_SHEET.cash[4], 'bn')} less borrowings ${fmtAmt(a.borrowings[4], 'bn')}`} tone="positive" />
        <Stat label="Cash conversion FY25" value={fmtPct(a.cashConversion[L])} sub="CFFO / EBITDA" />
        <Stat label="Borrowings drawn FY25" value={money(CASH_FLOW.drawdowns[3])} sub="mainly USD notes issued" tone="warning" />
        <Stat label="Dividend payable 30 Jun 26" value={money(BALANCE_SHEET.dividendPayable[4])} sub="of MYR 20.0 Bn declared Feb 2026" tone="warning" />
      </Section>

      <Section>
        <ChartCard title="Cash from operations against its uses" caption={`${UNITS[unit].label}. Capital investments include acquisitions and investments in joint ventures.`}>
          <ResponsiveContainer>
            <BarChart data={a.cashUses} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="cffo" name="Cash from operations" fill={c[0]} radius={BAR_END} />
              <Bar dataKey="capex" name="Capital investments" fill={c[1]} radius={BAR_END} />
              <Bar dataKey="dividends" name="Dividends to shareholder" fill={c[2]} radius={BAR_END} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Liquidity" caption={`${UNITS[unit].label}. Year-ends FY22–FY25, then 30 June 2026.`}>
          <ResponsiveContainer>
            <LineChart data={a.liquidity} margin={{ top: 16, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} domain={[0, 'auto']} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="cash" name="Cash" stroke={c[0]} strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="borrowings" name="Total borrowings" stroke={c[1]} strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="netCash" name="Net cash" stroke={c[2]} strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Contributions to the Government — FY2025" caption="MYR billion, from the FY2025 results presentation." height={260}>
          <ResponsiveContainer>
            <BarChart data={GOVT_CONTRIBUTIONS_FY25} layout="vertical" margin={{ left: 8, right: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} horizontal={false} />
              <XAxis type="number" {...axis} />
              <YAxis type="category" dataKey="label" {...axis} width={150} />
              <ChartTip formatter={(v) => [`MYR ${v.toFixed(1)} Bn`, 'Contribution']} />
              <Bar dataKey="value" fill={c[0]} radius={[0, 4, 4, 0]}>
                <LabelList dataKey="value" position="right" style={{ fill: theme.palette.text.secondary, fontSize: 11 }} formatter={(v) => v.toFixed(1)} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>
    </>
  );
};

const OperationsTab = () => {
  const axis = useAxis();
  const c = useSeries();
  const theme = useTheme();
  const o = OPERATIONS;
  const rows = OPS_PERIODS.map((p, i) => ({
    period: p,
    crude: o.productionCrude[i],
    gas: o.productionGas[i],
    lng: o.lngSalesMt[i],
    salesGas: o.salesGasMmscfd[i],
  }));
  const fy = rows.slice(0, 4);
  const marketRows = ['FY24', 'FY25', '1H25', '1H26'].map((p) => ({ period: p, ...MARKET[p] }));

  return (
    <>
      <Section title="Volumes" caption="Production includes Malaysia (all operators) and PETRONAS's international equity production. Half-year LNG volumes are six months, so compare 1H with 1H.">
        <Stat hero label="Production 1H26" value={`${o.productionTotal[5].toLocaleString()} kboe/d`} {...deltaProps(o.productionTotal[5], o.productionTotal[4])} sub="vs 1H25" tone="neutral" />
        <Stat hero label="LNG sales 1H26" value={`${o.lngSalesMt[5]} Mt`} {...deltaProps(o.lngSalesMt[5], o.lngSalesMt[4])} sub="LNG Canada at full operations" tone="positive" />
        <Stat hero label="Malaysia sales gas 1H26" value={`${o.salesGasMmscfd[5].toLocaleString()} mmscfd`} {...deltaProps(o.salesGasMmscfd[5], o.salesGasMmscfd[4])} sub="vs 1H25" tone="positive" />
        <Stat hero label="Chemicals sold FY25" value={`${o.chemicalsMt[3]} Mt`} {...deltaProps(o.chemicalsMt[3], o.chemicalsMt[2])} sub="YoY" />
      </Section>

      <Section>
        <ChartCard title="Production mix" caption="Thousand barrels of oil equivalent per day. Gas is rising while crude declines.">
          <ResponsiveContainer>
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} />
              <ChartTip formatter={(v, n) => [`${v.toLocaleString()} kboe/d`, n]} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="gas" name="Natural gas" stackId="p" fill={c[0]} stroke={theme.palette.background.paper} strokeWidth={2} />
              <Bar dataKey="crude" name="Crude & condensates" stackId="p" fill={c[1]} stroke={theme.palette.background.paper} strokeWidth={2} radius={BAR_END} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Gross LNG sales" caption="Million tonnes per financial year.">
          <ResponsiveContainer>
            <BarChart data={fy}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} domain={[0, 'auto']} />
              <ChartTip formatter={(v) => [`${v} Mt`, 'LNG sales']} />
              <Bar dataKey="lng" name="LNG sales" fill={c[2]} radius={BAR_END}>
                <LabelList dataKey="lng" position="top" style={{ fill: theme.palette.text.secondary, fontSize: 11 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>

      <Section title="Market backdrop" caption="Average prices and exchange rates from the results presentations. JCC prices are published prices, not realised prices.">
        <Card elevation={0} sx={{ flex: '1 1 100%', p: 2, borderRadius: RADIUS.panel, border: `1px solid ${theme.palette.divider}`, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Indicator</TableCell>
                {marketRows.map((r) => (
                  <TableCell key={r.period} align="right" sx={{ fontWeight: 700, fontSize: 12 }}>
                    {r.period}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ['Average Brent (US$/bbl)', 'brent'],
                ['Average JCC single-month (US$/bbl)', 'jcc'],
                ['Average USD/MYR', 'usdMyr'],
              ].map(([label, k]) => (
                <TableRow key={k}>
                  <TableCell sx={{ fontSize: 12.5 }}>{label}</TableCell>
                  {marketRows.map((r) => (
                    <TableCell key={r.period} align="right" sx={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                      {r[k].toFixed(2)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </Section>
    </>
  );
};

// ---------- statements ----------

const PL_LAYOUT = [
  ['revenue', 'Revenue'],
  ['costOfRevenue', 'Cost of revenue'],
  ['grossProfit', 'Gross profit', true],
  ['sellingDistribution', 'Selling and distribution expenses'],
  ['administration', 'Administration expenses'],
  ['impairments', 'Net impairment losses / write-off'],
  ['otherExpenses', 'Other expenses'],
  ['otherIncome', 'Other income'],
  ['operatingProfit', 'Operating profit', true],
  ['financingCosts', 'Financing costs'],
  ['shareOfAssociatesJvs', 'Share of profit / (loss) of associates & JVs'],
  ['profitBeforeTax', 'Profit before taxation', true],
  ['taxExpense', 'Tax expense'],
  ['patContinuing', 'Profit from continuing operations', true],
  ['patDiscontinued', 'Profit / (loss) from discontinued operations'],
  ['totalPat', 'Profit for the period', true],
];

const BS_LAYOUT = [
  ['ppe', 'Property, plant and equipment'],
  ['investmentProperties', 'Investment properties & land held for development'],
  ['associatesJvs', 'Investments in associates and joint ventures'],
  ['intangibles', 'Intangible assets'],
  ['longTermReceivables', 'Long-term receivables'],
  ['ncFundInvestments', 'Fund and other investments'],
  ['deferredTaxAssets', 'Deferred tax assets'],
  ['totalNonCurrentAssets', 'Total non-current assets', true],
  ['inventories', 'Trade and other inventories'],
  ['receivables', 'Trade and other receivables'],
  ['cFundInvestments', 'Fund and other investments'],
  ['cash', 'Cash and cash equivalents'],
  ['heldForSale', 'Assets classified as held for sale'],
  ['totalCurrentAssets', 'Total current assets', true],
  ['totalAssets', 'TOTAL ASSETS', true],
  ['shareholdersEquity', "Equity attributable to shareholders"],
  ['nci', 'Non-controlling interests'],
  ['totalEquity', 'TOTAL EQUITY', true],
  ['ncBorrowings', 'Borrowings — non-current'],
  ['deferredTaxLiabilities', 'Deferred tax liabilities'],
  ['otherLongTermLiabilities', 'Other long-term liabilities and provisions'],
  ['totalNonCurrentLiabilities', 'Total non-current liabilities', true],
  ['payables', 'Trade and other payables'],
  ['cBorrowings', 'Borrowings — current'],
  ['taxation', 'Taxation'],
  ['dividendPayable', 'Dividend payable'],
  ['heldForSaleLiabilities', 'Liabilities classified as held for sale'],
  ['totalCurrentLiabilities', 'Total current liabilities', true],
  ['totalLiabilities', 'TOTAL LIABILITIES', true],
];

const CF_LAYOUT = [
  ['operating', 'Net cash from operating activities', true],
  ['investing', 'Net cash used in investing activities', true],
  ['financing', 'Net cash from / (used in) financing activities', true],
  ['dividendsPaid', 'of which: dividends paid to shareholder'],
  ['dividendsToNci', 'of which: dividends paid to non-controlling interests'],
  ['drawdowns', 'of which: drawdown of borrowings'],
  ['repayments', 'of which: repayment of borrowings'],
];

const StatementsTab = () => {
  const theme = useTheme();
  return (
    <>
      <Section>
        <StatementTable
          title="Statement of profit or loss — continuing operations"
          caption="Discontinued operations shown as one line. FY23 is the restated comparative from the FY2024 report."
          periods={PL_PERIODS}
          layout={PL_LAYOUT}
          values={PROFIT_AND_LOSS}
          unitLabel="MYR million"
        />
        <StatementTable
          title="Statement of financial position"
          caption="Year-end positions at 31 December; 1H26 is at 30 June 2026."
          periods={BS_PERIODS}
          layout={BS_LAYOUT}
          values={BALANCE_SHEET}
          unitLabel="MYR million"
        />
        <StatementTable
          title="Statement of cash flows — summary"
          caption="Figures include discontinued operations where the report shows them."
          periods={PL_PERIODS}
          layout={CF_LAYOUT}
          values={CASH_FLOW}
          unitLabel="MYR million"
        />
      </Section>
      <Section title="Sources">
        <Card elevation={0} sx={{ flex: '1 1 100%', p: 2, borderRadius: RADIUS.panel, border: `1px solid ${theme.palette.divider}` }}>
          {Object.entries(YEAR_META).map(([p, m]) => (
            <Typography key={p} sx={{ fontSize: 12.5, py: 0.3 }}>
              <b>{p}</b> · period ended {m.endedOn} · {m.source}
            </Typography>
          ))}
          <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 1.5 }}>
            {COMPANY.legalName} ({COMPANY.registration}) · {COMPANY.basis} · figures as printed, in {COMPANY.units}.
            Ratios, free cash flow and net cash are calculated here and are not taken from the reports.
            Source: petronas.com/investor-relations/financial-results.
          </Typography>
        </Card>
      </Section>
    </>
  );
};

const TAB_VIEWS = {
  overview: OverviewTab,
  segments: SegmentsTab,
  cash: CashTab,
  operations: OperationsTab,
  statements: StatementsTab,
};

// ---------- shell ----------

const PetronasDashboard = ({ open, onClose }) => {
  // Static snapshot of published figures: a pure derivation, no fetch.
  const analytics = useMemo(() => buildAnalytics(), []);
  return (
    <DashboardShell
      open={open}
      onClose={onClose}
      title={`${COMPANY.name} — Financial Overview`}
      subtitle={`${COMPANY.basis} · FY2022–FY2025 and 1H 2026`}
      tabs={TABS}
      views={TAB_VIEWS}
      units={UNITS}
      defaultUnit="bn"
      analytics={analytics}
    />
  );
};

export default PetronasDashboard;
